/**
 * The four places the new API's wire format differs from our domain model.
 *
 * Everything else the Quoflow methods return is already domain-shaped, which is why there is no
 * mapper layer any more — just these.
 */

import type { Currency, Money } from '../types'

export interface WireList<T> {
  data: T[]
  total: number
  limit: number
  offset: number
}

/** FX arrives keyed `"<FROM>_<TO>"`, e.g. `{"USD_AED": {rate, date}}`. */
export interface WireFx {
  [pair: string]: { rate: number; date: string }
}

export function num(value: unknown, fallback = 0): number {
  const n = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(n) ? n : fallback
}

export function str(value: unknown, fallback = ''): string {
  return typeof value === 'string' && value !== '' ? value : fallback
}

/**
 * A DISPLAY string off the wire, trimmed. The live server returns a leading space on
 * `supplier_name` (and possibly other Data fields) — trimmed here, once, so a display value never
 * carries whitespace the docname (identity) doesn't. Previously duplicated at three call sites
 * (`useEnquiries.ts`'s `toRfq`, `useSuppliers.ts`'s `toSupplier`, and the now-deleted
 * `mappers/supplier.ts`); centralized here (task 12). Never apply this to an identity field —
 * trimming a docname is a different, unrelated concern this helper does not address.
 */
export function trimmed(value: unknown, fallback = ''): string {
  return str(value, fallback).trim()
}

/** Money on the wire is a bare number; the currency lives on the parent document. */
export function money(amount: unknown, currency: Currency): Money {
  return { amount: num(amount), currency }
}

/** Frappe `Check` fields are 0|1. Real booleans pass through. */
export function bool01(value: unknown): boolean {
  return value === true || value === 1 || value === '1'
}

/**
 * Rate to convert ONE unit of `from` into `to`. Returns `null` when no pair covers it — callers
 * must exclude the row rather than fall back to 1, which would silently compare a USD price
 * against an AED one.
 */
export function fxLookup(fx: WireFx, from: Currency, to: Currency): number | null {
  if (from === to) return 1
  const direct = fx[`${from}_${to}`]
  if (direct && Number.isFinite(direct.rate)) return direct.rate
  const inverse = fx[`${to}_${from}`]
  if (inverse && Number.isFinite(inverse.rate) && inverse.rate !== 0) return 1 / inverse.rate
  return null
}
